import React, { useState, useMemo, useEffect, useRef } from "react";
import { Link as RouterLink, useHistory } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import moment from "moment";
import { getRegistrationStatus } from "../../services/settings.api";

import {
  TextField,
  Link,
  Typography,
  Button,
  InputAdornment,
  IconButton,
  Checkbox,
  FormControlLabel,
} from "@material-ui/core";
import { Visibility, VisibilityOff } from "@material-ui/icons";

import { openApi } from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import AuthLayout, { useAuthFormStyles } from "../../components/AuthLayout";

/* ======================================================
   VALIDATION
====================================================== */
const UserSchema = Yup.object().shape({
  companyName: Yup.string().required(),
  name: Yup.string().required(),
  email: Yup.string().email().required(),
  phone: Yup.string().required(),
  password: Yup.string().min(8).required(),
  acceptedTerms: Yup.boolean().oneOf(
    [true],
    "Debes aceptar las Condiciones de Uso y la Política de Privacidad"
  ),
});

/* ======================================================
   COMPONENT — doc 0446, fase 3: misma lógica; la presentación vive en AuthLayout.
====================================================== */
const SignUp = () => {
  const classes = useAuthFormStyles();
  const history = useHistory();
  const isMounted = useRef(true);
  const [showPassword, setShowPassword] = useState(false);
  const [trial, setTrial] = useState(1); // ✅ Valor por defecto: 1 día

  const dueDate = useMemo(
    () => moment().add(trial, "day").format(),
    [trial]
  );

  // ✅ Verificar si el registro está habilitado usando endpoint público que verifica AMBAS configuraciones
  // ✅ También obtiene el valor dinámico de trial (días de prueba)
  useEffect(() => {
    isMounted.current = true;

    const checkRegistrationEnabled = async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        const response = await getRegistrationStatus({
          signal: controller.signal,
        });

        clearTimeout(timeoutId);
        if (!isMounted.current) return;

        // ✅ El registro debe estar habilitado si AMBAS configuraciones están en "enabled"
        const registrationEnabled = response?.data?.enabled === true;
        if (!registrationEnabled) {
          // Redirigir al login si el registro está deshabilitado
          history.push("/login");
          return;
        }

        // ✅ Obtener días de prueba desde la configuración (valor dinámico)
        const trialDays = response?.data?.trial || 1;
        if (trialDays && !isNaN(parseInt(trialDays, 10)) && parseInt(trialDays, 10) > 0) {
          setTrial(parseInt(trialDays, 10));
        }
      } catch (error) {
        if (!isMounted.current) return;
        // Si hay error, redirigir al login por seguridad
        history.push("/login");
      }
    };

    checkRegistrationEnabled();

    return () => {
      isMounted.current = false;
    };
  }, [history]);

  const handleSubmit = async (values) => {
    try {
      // No enviar planId fijo: el backend resuelve plan Free/Gratis o el primero válido (evita FK rota si id no existe en Plans).
      await openApi.post("/companies/cadastro", {
        name: values.companyName,
        ownerName: values.name,
        phone: values.phone,
        email: values.email,
        password: values.password,
        recurrence: "MENSAL",
        dueDate,
        status: true,
        campaignsEnabled: true,
      });
      toast.success("Cuenta creada correctamente");
      history.push("/login");
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <AuthLayout
      title={i18n.t("authScreens.signupTitle")}
      subtitle={i18n.t("authScreens.signupSubtitle", { days: trial })}
    >
      <Formik
        initialValues={{
          companyName: "",
          name: "",
          email: "",
          phone: "",
          password: "",
          acceptedTerms: false,
        }}
        validationSchema={UserSchema}
        onSubmit={handleSubmit}
      >
        {({ handleChange, handleBlur, errors, touched, values, setFieldValue }) => (
          <Form className={classes.form}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 16 }}>
              <TextField
                fullWidth
                variant="outlined"
                label={i18n.t("authScreens.company")}
                name="companyName"
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.companyName && Boolean(errors.companyName)}
                helperText={touched.companyName && errors.companyName}
                className={classes.field}
              />
              <TextField
                fullWidth
                variant="outlined"
                label={i18n.t("authScreens.name")}
                name="name"
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name && errors.name}
                className={classes.field}
              />
            </div>

            <TextField
              fullWidth
              variant="outlined"
              label={i18n.t("login.form.email")}
              name="email"
              type="email"
              autoComplete="email"
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.email && Boolean(errors.email)}
              helperText={touched.email && errors.email}
              className={classes.field}
            />

            <TextField
              fullWidth
              variant="outlined"
              label={i18n.t("authScreens.whatsapp")}
              name="phone"
              inputMode="tel"
              autoComplete="tel"
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.phone && Boolean(errors.phone)}
              helperText={touched.phone && errors.phone}
              className={classes.field}
            />

            <TextField
              fullWidth
              variant="outlined"
              label={i18n.t("authScreens.password")}
              name="password"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.password && Boolean(errors.password)}
              helperText={touched.password && errors.password}
              className={classes.field}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      aria-label={i18n.t("authScreens.showPassword")}
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <div>
              <FormControlLabel
                control={
                  <Checkbox
                    name="acceptedTerms"
                    color="primary"
                    size="small"
                    checked={values.acceptedTerms}
                    onChange={(e) => setFieldValue("acceptedTerms", e.target.checked)}
                    onBlur={handleBlur}
                  />
                }
                label={
                  <Typography variant="body2" style={{ fontSize: 13, lineHeight: 1.4 }}>
                    {i18n.t("authScreens.acceptPrefix")}{" "}
                    <Link component={RouterLink} to="/terms" target="_blank" className={classes.link}>
                      {i18n.t("authScreens.terms")}
                    </Link>{" "}
                    {i18n.t("authScreens.and")}{" "}
                    <Link component={RouterLink} to="/privacy" target="_blank" className={classes.link}>
                      {i18n.t("authScreens.privacy")}
                    </Link>
                  </Typography>
                }
              />
              {touched.acceptedTerms && errors.acceptedTerms && (
                <Typography variant="caption" color="error" style={{ display: "block", marginLeft: 12 }}>
                  {errors.acceptedTerms}
                </Typography>
              )}
            </div>

            <Button type="submit" fullWidth variant="contained" className={classes.submit}>
              {i18n.t("authScreens.signupButton")}
            </Button>

            <Typography className={classes.muted}>
              {i18n.t("authScreens.haveAccount")}{" "}
              <Link component={RouterLink} to="/login" className={classes.link}>
                {i18n.t("authScreens.signIn")}
              </Link>
            </Typography>
          </Form>
        )}
      </Formik>
    </AuthLayout>
  );
};

export default SignUp;
